import {
    FEATURE_NR_CALL_STATUS,
    QUICK_CHECKOUT_NB_STATE,
    buildNRLatencyMs,
    recordQuickCheckoutNativeBridgeCall,
    recordQuickCheckoutNav,
    QUICK_CHECKOUT_NAV_DIRECTION,
    getBridgeSdk,
    CheckoutActionTypes,
    CheckoutState,
    isQuickCheckoutFlow,
    QC_FOOTER_TYPE,
    QC_FOOTER_TYPE_PAYLOAD,
    isAipFlow,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    X_CART_TYPE,
} from "@swiggy-private/checkout-core";
import { isValidNumber, randomStringIdGenerator } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { ExitFlowType, RefreshTypes, WebviewLoadedType } from "@swiggy-private/native-bridge";
import {
    MARKETPLACE_BUSINESS_LINE,
    MARKETPLACE_CATEGORY,
    MARKETPLACE_ID,
    MRONativeLocalCart,
    QuickCheckoutFunctionalErrorPresentationalData,
    MROReviewedCartApiResponse,
} from "@swiggy-private/cart-types";
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import { isEmptyMROCart, normaliseId } from "@cart-client/helpers/MROFoodCart";
import { getSelectedAddress } from "@cart-client/helpers/CartFooter";
import { QC_ERROR_FOOTER_FALLBACK, AIP_TURBO_ANALYTICS_VARIANT } from "@cart-client/constants/QuickCheckout";
import { CartActionTypes } from "@cart-client/reducers/CartReducer";
import { MROCartAction } from "@cart-client/reducers/MROCartReducer";

import { prepareInternalWebLocalCartPayloadFromServerAndLocalCart } from "./MROFoodCartPayloadPrep";

// Re-exported from checkout-core (single source of truth for the quick-checkout x-cart-type check)
// so cart-client consumers keep importing it from this helper path.
export { isQuickCheckoutFlow };
export { isAipFlow };

/**
 * Consumes the one-shot QC prefetch handoff on `window.cartApiData`.
 * Must run after loading/failed/success (or drop) is applied so MRO→QC remount
 * cannot replay a stale `quickCheckoutCartApiStatus` (e.g. "loading") over CartContext.
 */
export const clearQuickCheckoutPrefetch = (): void => {
    if (typeof window === "undefined" || !window.cartApiData) {
        return;
    }
    window.cartApiData.data = undefined;
    window.cartApiData.source = undefined;
    window.cartApiData.needsBackgroundRefresh = false;
    window.cartApiData.mroPreCartSelectionMode = undefined;
    window.cartApiData.nativeLocalCart = undefined as unknown as MRONativeLocalCart;
    window.cartApiData.quickCheckoutCartApiStatus = undefined;
};

/**
 * Parses the epoch-ms cart-update timestamp from either transport.
 * Both direct-bridge prefetch and global JS bridge use `last_updated_timestamp`.
 */
export const parseQuickCheckoutCartUpdateTimestamp = (value?: string | number | null): number | null => {
    if (isEmpty(value) || !isValidNumber(value)) {
        return null;
    }
    const timestamp = typeof value === "number" ? value : parseInt(String(value), 10);
    return Number.isFinite(timestamp) ? timestamp : null;
};

/**
 * Whether an incoming success cart update should replace the currently applied one.
 * Missing/invalid incoming timestamps are always rejected (contract requires last_updated_timestamp).
 * First valid success always wins; afterwards only a strictly newer timestamp is accepted.
 */
export const isNewerQuickCheckoutCartUpdate = (
    incoming?: string | number | null,
    currentAppliedAt: number | null = null,
): boolean => {
    const incomingTs = parseQuickCheckoutCartUpdateTimestamp(incoming);
    if (incomingTs === null) {
        return false;
    }
    if (currentAppliedAt === null) {
        return true;
    }
    return incomingTs > currentAppliedAt;
};

export const hasQuickCheckoutErrorData = (
    errorData?: QuickCheckoutFunctionalErrorPresentationalData | null,
): boolean => {
    if (isEmpty(errorData)) {
        return false;
    }
    return (
        !isEmpty(errorData?.statusMessage) ||
        !isEmpty(errorData?.statusTitle) ||
        !isEmpty(errorData?.ctaText) ||
        !isEmpty(errorData?.ctaAction)
    );
};

export const getQuickCheckoutErrorData = (
    cartResponse?: MROReviewedCart | null,
): QuickCheckoutFunctionalErrorPresentationalData | undefined => {
    const errorData = cartResponse?.data?.collapsed_cart_presentational_data?.error_data;
    if (!hasQuickCheckoutErrorData(errorData)) {
        return undefined;
    }

    return {
        statusTitle: errorData?.statusTitle || QC_ERROR_FOOTER_FALLBACK.statusTitle,
        statusMessage: errorData?.statusMessage || QC_ERROR_FOOTER_FALLBACK.statusMessage,
        ctaText: errorData?.ctaText || QC_ERROR_FOOTER_FALLBACK.ctaText,
        ctaAction: errorData?.ctaAction || QC_ERROR_FOOTER_FALLBACK.ctaAction,
    };
};

export const hasActiveQuickCheckoutError = (
    statusCode: CART_STATUS_CODES,
    errorData?: QuickCheckoutFunctionalErrorPresentationalData | null,
): boolean => statusCode !== CART_STATUS_CODES.SUCCESS && hasQuickCheckoutErrorData(errorData);

export const getQuickCheckoutErrorWidgetProps = (
    hasQcErrorData: boolean,
    qcErrorData: QuickCheckoutFunctionalErrorPresentationalData | undefined | null,
    fallback: {
        statusMessage?: string;
        ctaText?: string;
        ctaAction?: string;
        titleMessage?: string;
    },
): {
    statusMessage?: string;
    ctaText?: string;
    ctaAction?: string;
    titleMessage?: string;
} => ({
    statusMessage: hasQcErrorData
        ? qcErrorData?.statusMessage || QC_ERROR_FOOTER_FALLBACK.statusMessage
        : fallback.statusMessage,
    ctaText: hasQcErrorData ? qcErrorData?.ctaText || QC_ERROR_FOOTER_FALLBACK.ctaText : fallback.ctaText,
    ctaAction: hasQcErrorData ? qcErrorData?.ctaAction || QC_ERROR_FOOTER_FALLBACK.ctaAction : fallback.ctaAction,
    titleMessage: hasQcErrorData
        ? qcErrorData?.statusTitle || QC_ERROR_FOOTER_FALLBACK.statusTitle
        : fallback.titleMessage,
});

export const isQuickCheckoutHardFailure = (reviewedCart?: MROReviewedCart | null): boolean => {
    return reviewedCart?.statusCode === CART_STATUS_CODES.FAILURE && isEmptyMROCart(reviewedCart?.data);
};

/**
 * Builds the CartContext `UPDATE_CART` action that promotes the app-owned reviewed +
 * native local cart for quick checkout.
 *
 * Returns `null` when there is no usable cart (empty / childless) so callers can skip
 * dispatching. Shared by both the direct-bridge (prefetch inline) and JS-bridge flows
 * so the promotion logic never drifts, and it keeps the promotion out of
 * `QuickCheckoutContext` — that context stays focused on the lifecycle status while
 * `CartContext` remains the single source of truth for cart data.
 */
export const buildQuickCheckoutUpdateCartAction = (
    reviewedCart?: MROReviewedCart | null,
    nativeLocalCart?: MRONativeLocalCart | null,
): MROCartAction | null => {
    // in case of hard failure, we need to clear the local cart for apps
    // so they can perform flush cart operation
    if (isQuickCheckoutHardFailure(reviewedCart)) {
        return {
            type: CartActionTypes.CLEAR_LOCAL_CART,
            payload: {
                reviewedCart: reviewedCart as MROReviewedCart,
            },
        };
    }

    if (isEmpty(reviewedCart) || isEmptyMROCart(reviewedCart?.data)) {
        return null;
    }

    return {
        type: CartActionTypes.UPDATE_CART,
        payload: {
            uniqueIdForCart: randomStringIdGenerator(),
            localCart: prepareInternalWebLocalCartPayloadFromServerAndLocalCart(
                reviewedCart,
                nativeLocalCart ?? undefined,
            ),
            reviewedCart: reviewedCart as MROReviewedCart,
        },
    };
};

export const sendQuickCheckoutLoadedNativeSignal = (): void => {
    getBridgeSdk()
        .webviewLoaded({
            webviewId: WebviewLoadedType.QUICK_CHECKOUT,
        })
        .then(() => {
            // no-op
        })
        .catch((_error) => {
            // no-op
        });
};

export const refreshQuickCheckoutParentCollection = (): void => {
    // Fire-and-forget: callers do not await this callback; skip NR success/failure noise.
    getBridgeSdk()
        .refreshScreen({
            type: RefreshTypes.PARENT_SCREEN,
        })
        .then(() => {
            // no-op
        })
        .catch(() => {
            // no-op
        });
};

export const sendNativeSignalToCloseWebviewForQC = (): void => {
    recordQuickCheckoutNav({ direction: QUICK_CHECKOUT_NAV_DIRECTION.QC_EXIT });
    const startMs = Date.now();
    getBridgeSdk()
        .exitFlow({
            flowType: ExitFlowType.QUICK_CHECKOUT,
        })
        .then(() => {
            recordQuickCheckoutNativeBridgeCall({
                nbState: QUICK_CHECKOUT_NB_STATE.EXIT_FLOW,
                status: FEATURE_NR_CALL_STATUS.SUCCESS,
                latencyMs: buildNRLatencyMs(startMs),
            });
        })
        .catch(() => {
            // Bridge / native already surface exit failures — avoid duplicate NR.
        });
};

/**
 * Tells whether an outgoing update-cart payload is changing the delivery address.
 *
 * Every update-cart caller spreads the whole local cart, so `address_id` is always present
 * in the payload — only the value diff separates an address selection from any other cart
 * update. Ids reach the client both as numbers (native bridge) and strings (call sites that
 * stringify), so both sides are normalised before comparing. An empty incoming id can never
 * be a user address selection, so it is not reported as a change.
 */
export const hasCartAddressChanged = (
    incomingAddressId?: string | number | null,
    currentAddressId?: string | number | null,
): boolean => {
    const incoming = normaliseId(incomingAddressId);
    if (isEmpty(incoming)) {
        return false;
    }
    return incoming !== normaliseId(currentAddressId);
};

/** True when full-cart address UI was opened from QC and we should return to QC after. */
export const isOpenedAddressFromQuickCheckout = (checkoutState: CheckoutState): boolean => {
    if (!isQuickCheckoutFlow() || checkoutState.type !== CheckoutActionTypes.SHOW_MRO_FOOD_CART) {
        return false;
    }
    return !!checkoutState.payload?.openedAddressFromQuickCheckout;
};

/**
 * Flow-specific analytics `variant` from `x-cart-type`. QC is the surface;
 * AIP is one cart type that can enable it. New QC flows add a case here.
 */
const getQuickCheckoutAnalyticsVariant = (): { variant: string } | Record<string, never> => {
    switch ((GlobalHeaderState.getHeader(SwgyHeaderKeysList.xCartType) || "").toLowerCase()) {
        case X_CART_TYPE.AIP:
            return { variant: AIP_TURBO_ANALYTICS_VARIANT };
        default:
            return {};
    }
};

/**
 * Prebuilt QC turbo analytics fields (snake_case) for event context.
 * Built once in cart; payment only spreads this bag.
 */
export const buildQuickCheckoutCartContextForAnalytics = (
    cartData: MROReviewedCartApiResponse | null | undefined,
): Record<string, unknown> => ({
    ...getQuickCheckoutAnalyticsVariant(),
    savings: cartData?.collapsed_cart_presentational_data?.collapsed_cart_discount_data?.savings_data?.total_discount,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    to_pay: cartData?.cart_total,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cart_id: cartData?.cartId,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    parent_cart_id: cartData?.originalCartId,
});

// for setting height for quick checkout footer
export const getQuickCheckoutWebviewScale = (
    reviewedCart?: MROReviewedCart | null,
    isLoading = false,
): QC_FOOTER_TYPE => {
    // Only the first load (shimmer, no cart yet) uses the loading scale — mirrors the renderer's
    // shimmer condition. Later updates render in place, so resizing would clip the visible footer.
    if (isLoading && isEmpty(reviewedCart)) {
        return QC_FOOTER_TYPE.LOADING;
    }

    const statusCode = reviewedCart?.statusCode;
    const selectedAddress = getSelectedAddress(reviewedCart?.data?.addresses);

    if (statusCode === CART_STATUS_CODES.SUCCESS) {
        if (!selectedAddress) {
            return QC_FOOTER_TYPE.SHORT_ERROR_FOOTER;
        }
        return QC_FOOTER_TYPE.DEFAULT;
    }

    if (statusCode === CART_STATUS_CODES.ITEMS_UNAVAILABLE || statusCode === CART_STATUS_CODES.ALL_UNSERVICEABLE) {
        return QC_FOOTER_TYPE.MEDIUM_ERROR_FOOTER;
    }

    if (statusCode === CART_STATUS_CODES.LOCATION_UNSERVICEABLE) {
        return QC_FOOTER_TYPE.SHORT_ERROR_FOOTER;
    }

    if (hasQuickCheckoutErrorData(getQuickCheckoutErrorData(reviewedCart))) {
        return QC_FOOTER_TYPE.ERROR_FOOTER;
    }

    return QC_FOOTER_TYPE.DEFAULT;
};

export const getQuickCheckoutContentHeight = (reviewedCart?: MROReviewedCart | null, isLoading = false): number => {
    return QC_FOOTER_TYPE_PAYLOAD[getQuickCheckoutWebviewScale(reviewedCart, isLoading)].height;
};

export const clearQuickCheckoutLocalCart = () => {
    getBridgeSdk()
        .clearLocalCart({
            tenant: {
                marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.REGULAR,
                marketplaceCategory: MARKETPLACE_CATEGORY.FOOD,
                marketplaceId: MARKETPLACE_ID.SWIGGY,
                cartType: X_CART_TYPE.AIP,
            },
        })
        .catch(() => {
            // no-op
        });
};
